import React, { useState } from 'react';
import { Settings, AlertTriangle, Gauge } from 'lucide-react';

const OperatingEnvelopes = () => {
  const [selectedEquipment, setSelectedEquipment] = useState('reactor');

  const equipmentEnvelopes = {
    reactor: {
      name: 'CSTR Reactor R-201',
      tag: 'R-201',
      parameters: [
        {
          name: 'Temperature',
          unit: '°C',
          normal: { min: 175, max: 185 },
          operating: { min: 170, max: 190 },
          safe: { min: 160, max: 200 },
          current: 180,
          status: 'normal'
        },
        {
          name: 'Pressure',
          unit: 'bar',
          normal: { min: 14, max: 16 },
          operating: { min: 12, max: 18 },
          safe: { min: 10, max: 20 },
          current: 15.2,
          status: 'normal'
        },
        {
          name: 'Level',
          unit: '%',
          normal: { min: 60, max: 80 },
          operating: { min: 50, max: 90 },
          safe: { min: 40, max: 95 },
          current: 72,
          status: 'normal'
        },
        {
          name: 'Flow Rate',
          unit: 'm³/h',
          normal: { min: 45, max: 55 },
          operating: { min: 40, max: 60 },
          safe: { min: 30, max: 70 },
          current: 50,
          status: 'normal'
        }
      ]
    },
    column: {
      name: 'Distillation Column T-101',
      tag: 'T-101',
      parameters: [
        {
          name: 'Top Temperature',
          unit: '°C',
          normal: { min: 85, max: 95 },
          operating: { min: 80, max: 100 },
          safe: { min: 75, max: 110 },
          current: 90,
          status: 'normal'
        },
        {
          name: 'Bottom Temperature',
          unit: '°C',
          normal: { min: 155, max: 165 },
          operating: { min: 150, max: 170 },
          safe: { min: 145, max: 180 },
          current: 160,
          status: 'normal'
        },
        {
          name: 'Pressure',
          unit: 'bar',
          normal: { min: 4.5, max: 5.5 },
          operating: { min: 4, max: 6 },
          safe: { min: 3, max: 7 },
          current: 5.0,
          status: 'normal'
        },
        {
          name: 'Reflux Ratio',
          unit: '',
          normal: { min: 2.8, max: 3.2 },
          operating: { min: 2.5, max: 3.5 },
          safe: { min: 2.0, max: 4.0 },
          current: 3.0,
          status: 'normal'
        }
      ]
    }
  };

  const equipment = [
    { id: 'reactor', name: 'CSTR Reactor R-201', status: 'normal' },
    { id: 'column', name: 'Distillation Column T-101', status: 'normal' }
  ];

  const currentEquipment = equipmentEnvelopes?.[selectedEquipment];

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const calculatePosition = (value, safe) => {
    const range = safe?.max - safe?.min;
    const position = ((value - safe?.min) / range) * 100;
    return Math.max(0, Math.min(100, position));
  };

  return (
    <div className="space-y-6">
      {/* Equipment Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4 overflow-x-auto">
          {equipment?.map((eq) => (
            <button
              key={eq?.id}
              onClick={() => setSelectedEquipment(eq?.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors ${
                selectedEquipment === eq?.id
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span className="font-medium text-sm">{eq?.name}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Equipment Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentEquipment?.name}</h2>
            <p className="text-sm text-gray-600 mt-1">Tag: {currentEquipment?.tag}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Gauge className="w-6 h-6 text-green-600" />
            <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
              All Parameters Normal
            </span>
          </div>
        </div>
      </div>
      {/* Operating Parameters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {currentEquipment?.parameters?.map((param, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{param?.name}</h3>
                <p className="text-sm text-gray-600">Current: <span className="font-semibold text-gray-900">{param?.current} {param?.unit}</span></p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(param?.status)}`}>
                Normal
              </span>
            </div>

            {/* Visual Range Indicator */}
            <div className="mb-6">
              <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                {/* Safe Range */}
                <div 
                  className="absolute top-0 bottom-0 bg-green-200"
                  style={{
                    left: '0%',
                    right: '0%'
                  }}
                />
                {/* Operating Range */}
                <div 
                  className="absolute top-0 bottom-0 bg-blue-200"
                  style={{
                    left: `${((param?.operating?.min - param?.safe?.min) / (param?.safe?.max - param?.safe?.min)) * 100}%`,
                    right: `${((param?.safe?.max - param?.operating?.max) / (param?.safe?.max - param?.safe?.min)) * 100}%`
                  }}
                />
                {/* Normal Range */}
                <div 
                  className="absolute top-0 bottom-0 bg-green-400"
                  style={{
                    left: `${((param?.normal?.min - param?.safe?.min) / (param?.safe?.max - param?.safe?.min)) * 100}%`,
                    right: `${((param?.safe?.max - param?.normal?.max) / (param?.safe?.max - param?.safe?.min)) * 100}%`
                  }}
                />
                {/* Current Value Indicator */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-gray-900"
                  style={{
                    left: `${calculatePosition(param?.current, param?.safe)}%`
                  }}
                />
              </div>

              {/* Legend */}
              <div className="flex justify-between mt-2 text-xs text-gray-600">
                <span>{param?.safe?.min} {param?.unit}</span>
                <span>{param?.safe?.max} {param?.unit}</span>
              </div>
            </div>

            {/* Range Details */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-900">Normal Range</span>
                </div>
                <span className="text-sm text-gray-700">{param?.normal?.min} - {param?.normal?.max} {param?.unit}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-200 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-900">Operating Range</span>
                </div>
                <span className="text-sm text-gray-700">{param?.operating?.min} - {param?.operating?.max} {param?.unit}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-900">Safe Limits</span>
                </div>
                <span className="text-sm text-gray-700">{param?.safe?.min} - {param?.safe?.max} {param?.unit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Operating Envelope Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-4 h-4 bg-green-400 rounded mt-1"></div>
            <div>
              <p className="font-semibold text-gray-900">Normal Operating Range</p>
              <p className="text-sm text-gray-600">Target operating conditions for optimal performance</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-4 h-4 bg-blue-200 rounded mt-1"></div>
            <div>
              <p className="font-semibold text-gray-900">Extended Operating Range</p>
              <p className="text-sm text-gray-600">Acceptable operating conditions with monitoring</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-4 h-4 bg-red-200 rounded mt-1"></div>
            <div>
              <p className="font-semibold text-gray-900">Safe Operating Limits</p>
              <p className="text-sm text-gray-600">Maximum/minimum limits - alarms activated beyond these</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatingEnvelopes;